<script setup lang="ts">
import { defineProps, computed } from "vue";
import { Avatar, AvatarFallback, AvatarImage } from '@/shadcn/components/ui/avatar'

/**
 * Avatar component that displays either an image or initials fallback
 * with dynamic styling based on size and username.
 */
const props = defineProps({
  /** Username to generate initials from and use as alt text */
  username: {
    type: String,
    required: true,
  },
  /** Size of the avatar in pixels. Affects text size and border radius */
  size: {
    type: Number,
    default: 50,
  },
  /** Optional image source URL. If not provided, falls back to initials */
  imgSrc: {
    type: String,
    default: "",
  },
});


/**
 * Predefined set of Tailwind background and text colors.
 * Each username gets a consistent color pair based on a hash of the username.
 * Background uses 400 shade for a softer look, text uses 900 for contrast.
 */
const backgroundColors = [
  { bg: 'tw:bg-blue-400', text: 'tw:text-blue-900' },
  { bg: 'tw:bg-green-400', text: 'tw:text-green-900' },
  { bg: 'tw:bg-purple-400', text: 'tw:text-purple-900' },
  { bg: 'tw:bg-pink-400', text: 'tw:text-pink-900' },
  { bg: 'tw:bg-indigo-400', text: 'tw:text-indigo-900' },
  { bg: 'tw:bg-red-400', text: 'tw:text-red-900' },
  { bg: 'tw:bg-yellow-400', text: 'tw:text-yellow-900' },
  { bg: 'tw:bg-teal-400', text: 'tw:text-teal-900' },
  { bg: 'tw:bg-orange-400', text: 'tw:text-orange-900' },
  { bg: 'tw:bg-cyan-400', text: 'tw:text-cyan-900' },
];

/**
 * Generates a consistent numeric hash for a given string.
 * Used to deterministically select a color for each username.
 */
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Generates initials from the username.
 * - Strips non-letter characters
 * - Takes first letter of first two words
 * - Converts to uppercase
 */
const initials = computed(() => {
  // Remove all non-letter characters and split into words
  const words = props.username.replace(/[^a-zA-Z]/g, ' ').trim().split(/\s+/);
  
  // Get initials from first two words (or just first word if only one exists)
  const firstInitial = words[0] ? words[0][0].toUpperCase() : '';
  const secondInitial = words[1] ? words[1][0].toUpperCase() : '';
  
  return firstInitial + secondInitial;
});

/**
 * Determines text size based on avatar size:
 * - ≥40px: xl
 * - ≥30px: md
 * - ≥25px: sm
 * - <25px: 0.60rem
 */
const textSize = computed(() => {
  if (props.size >= 40) return 'tw:text-xl';
  if (props.size >= 30) return 'tw:text-md';
  if (props.size >= 25) return 'tw:text-sm';
  return 'tw:text-[0.60rem]';
});

/**
 * Determines border radius based on avatar size:
 * - ≥40px: lg
 * - ≥30px: md
 * - ≥25px: sm
 * - <25px: xs
 */
const borderRadius = computed(() => {
  if (props.size >= 40) return 'tw:rounded-lg';
  if (props.size >= 30) return 'tw:rounded-md';
  if (props.size >= 25) return 'tw:rounded-sm';
  return 'tw:rounded-xs';
});

/**
 * Selects a consistent color pair for the username.
 * Uses the hash of the username to pick from the predefined colors.
 */
const avatarColors = computed(() => {
  const hash = hashString(props.username);
  const colorIndex = hash % backgroundColors.length;
  return backgroundColors[colorIndex];
});

</script>

<template>
  <Avatar :class="borderRadius" :style="{ width: `${props.size}px`, height: `${props.size}px` }">
    <AvatarImage v-if="imgSrc" :src="imgSrc" :alt="username" />
    <AvatarFallback :class="[borderRadius, 'tw:font-semibold', textSize, avatarColors.bg, avatarColors.text]">{{ initials }}</AvatarFallback>
  </Avatar>
</template>

